import type { FeatureFlags, FeatureKey } from '@/types';

/**
 * Canonical feature/module registry — the ONE place feature metadata is defined. Both the merchant admin
 * ("Enabled modules") and the Super Admin console (Feature Registry, Plans, Tenant Overrides) read this;
 * no component should hardcode its own feature list or labels.
 */
export type FeatureCategory = 'Commerce' | 'Retention' | 'Marketing' | 'Insights';
export const FEATURE_CATEGORIES: FeatureCategory[] = ['Commerce', 'Retention', 'Marketing', 'Insights'];

export interface FeatureDef {
  key: FeatureKey;
  label: string;
  description: string;
  category: FeatureCategory;
  /** Core commerce plumbing every plan includes; shown as read-only "always on" in plan/override editors. */
  core?: boolean;
}

export const FEATURE_REGISTRY: FeatureDef[] = [
  { key: 'products', label: 'Products', description: 'Catalogue of products merchants sell.', category: 'Commerce', core: true },
  { key: 'categories', label: 'Categories', description: 'Group products into browsable categories.', category: 'Commerce', core: true },
  { key: 'cart', label: 'Cart & checkout', description: 'Shopping cart and the checkout flow.', category: 'Commerce', core: true },
  { key: 'orders', label: 'Orders', description: 'Order management and the customer order history.', category: 'Commerce', core: true },
  { key: 'payments', label: 'Online payments', description: 'Card / UPI / netbanking at checkout. Cash on delivery is always available.', category: 'Commerce' },
  { key: 'wallet', label: 'Wallet', description: 'Store-credit wallet: top-ups, refunds and spending at checkout.', category: 'Retention' },
  { key: 'loyalty', label: 'Loyalty', description: 'Points earned on purchases and redeemed for rewards.', category: 'Retention' },
  { key: 'affiliate', label: 'Affiliate', description: 'Referral links, commissions and payouts.', category: 'Retention' },
  { key: 'referrals', label: 'Referrals (legacy)', description: 'Superseded by Affiliate. Kept for backward compatibility; not wired to any screen.', category: 'Retention' },
  { key: 'coupons', label: 'Coupons & offers', description: 'Discount codes and automatic promotional offers.', category: 'Marketing' },
  { key: 'banners', label: 'Banners', description: 'Homepage and promotional banner artwork.', category: 'Marketing' },
  { key: 'campaigns', label: 'Campaigns', description: 'Email / SMS / push marketing campaigns to customer segments.', category: 'Marketing' },
  { key: 'reviews', label: 'Reviews', description: 'Customer ratings and reviews on product pages.', category: 'Marketing' },
  { key: 'analytics', label: 'Analytics', description: 'Reports: revenue, funnels, category and product performance.', category: 'Insights' },
];

export const featureDef = (key: FeatureKey) => FEATURE_REGISTRY.find((f) => f.key === key);
export const featureLabel = (key: FeatureKey) => featureDef(key)?.label ?? key;

/** undefined/absent = inherit the plan's default; true/false = an explicit platform override for that tenant. */
export type FeatureOverrides = Partial<Record<FeatureKey, boolean>>;
export type FeatureSource = 'plan' | 'override';

export const featureSource = (overrides: FeatureOverrides, key: FeatureKey): FeatureSource => (key in overrides ? 'override' : 'plan');

/** Plan Feature + Tenant Override = Effective Feature. The single function every effective-flags computation must use. */
export function computeEffectiveFeatures(planFeatures: FeatureFlags, overrides: FeatureOverrides): FeatureFlags {
  const out = { ...planFeatures };
  (Object.keys(overrides) as FeatureKey[]).forEach((k) => {
    const v = overrides[k];
    if (v !== undefined) out[k] = v;
  });
  return out;
}
